import Markdoc, { type RenderableTreeNode } from "@markdoc/markdoc";
import { type ComponentType, createElement, Fragment, type ReactNode } from "react";

import { AccordionItem, MarkdocAccordion } from "./Accordion";
import { Alert } from "./Alert";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Card, CardGroup } from "./Card";
import { CodeBlock } from "./CodeBlock";
import { CodeGroup } from "./CodeGroup";
import { Heading } from "./Heading";
import { Hero } from "./Hero";
import { Iframe } from "./Iframe";
import { Image } from "./Image";
import { InlineCode } from "./InlineCode";
import { Link } from "./Link";
import { Paragraph } from "./Paragraph";
import { Properties, Property } from "./Properties";
import { Step, Steps } from "./Steps";
import { MarkdocTable, TableTbody, TableTd, TableTh, TableThead, TableTr } from "./Table";
import { MarkdocTabs, Tab } from "./Tabs";

/**
 * @mountayaapp/markdoc/renderers is the JSX subpath (a sanctioned web-rendering
 * carve-out): it maps every Markdoc render name to a Mantine component and
 * exposes renderTree. Only the docs app imports it. markdoc is exempt from the
 * render-free JSX ban, so these renderers may use Mantine and Nucleo.
 */

export { ClientOnly } from "./ClientOnly";
export { Hero } from "./Hero";
export { createMarkdocShikiAdapter, shikiAdapter } from "./shikiAdapter";

/**
 * components maps Markdoc render names to their React components.
 */
const components = {
  Accordion: MarkdocAccordion,
  AccordionItem,
  Alert,
  Badge,
  Button,
  Card,
  CardGroup,
  CodeBlock,
  CodeGroup,
  Heading,
  Hero,
  Iframe,
  Image,
  InlineCode,
  Link,
  Paragraph,
  Properties,
  Property,
  Step,
  Steps,
  Tab,
  Table: MarkdocTable,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Tabs: MarkdocTabs,
};

/**
 * RenderTreeOptions tunes a renderTree call. `components` injects extra render
 * names — the seam the docs app uses to supply heavy, environment-dependent
 * renderers (GraphiQL, Redoc, the Studio embed) without bloating this package.
 * Injected entries override the built-in map on name collision.
 */
export interface RenderTreeOptions {
  components?: Record<string, ComponentType<Record<string, unknown>>>;
}

/**
 * renderTree turns a transformed Markdoc tree into React nodes using the shared
 * component map. Optional `components` are merged over the built-ins so the docs
 * app can inject heavy renderers; with no options the default mapping is used.
 */
export const renderTree = (tree: RenderableTreeNode, options?: RenderTreeOptions): ReactNode =>
  Markdoc.renderers.react(
    tree,
    { createElement, Fragment },
    { components: { ...components, ...options?.components } },
  );
